import chroma from 'chroma-js';
import { getReadableButtonColor } from './buttonContrast';
import { darkSurfaces } from './darkSurfaces';

// Restrained glass for workspace chrome: compact corners, stable geometry,
// and opaque fallbacks. Dense block content does not need its own blur layer.
export const glassChromeRadii = { surface: 10, modal: 12, control: 6, group: 8 };

const chromeColors = (theme) => {
  const dark = theme.palette.mode === 'dark';
  const primary = chroma.valid(theme.palette.primary.main) ? theme.palette.primary.main : '#2C5038';
  const solid = dark ? darkSurfaces.panel : '#F7F8F6';
  const ink = getReadableButtonColor(chroma(primary).hex(), solid);
  return {
    dark, ink,
    solid,
    tint: dark ? 'transparent' : chroma(primary).alpha(0.025).css(),
    rim: dark ? darkSurfaces.rim : 'rgba(52,68,58,0.12)',
    highlight: dark ? darkSurfaces.highlight : 'rgba(255,255,255,0.9)',
  };
};

export const glassChromeSurface = (theme, { radius = glassChromeRadii.surface, blur = false, elevated = false } = {}) => {
  const { dark, solid, tint, rim, highlight } = chromeColors(theme);
  return {
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
    border: `1px solid ${rim}`,
    borderRadius: `${radius}px`,
    backgroundColor: solid,
    backgroundImage: dark ? darkSurfaces.sheen : `linear-gradient(150deg, ${highlight}, transparent 55%), linear-gradient(${tint}, ${tint})`,
    boxShadow: `inset 0 1px 0 ${highlight}, inset 0 -1px 0 ${dark ? '#00000020' : '#FFFFFF70'}, ${elevated ? `0 16px 48px ${dark ? '#00000050' : '#1D2B241F'}` : `0 2px 8px ${dark ? '#00000016' : '#1D2B2407'}`}`,
    ...(blur && {
      '@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
        backgroundColor: dark ? darkSurfaces.glass : 'rgba(255,255,255,0.82)',
        backdropFilter: `blur(18px) saturate(${dark ? 110 : 125}%)`,
        WebkitBackdropFilter: `blur(18px) saturate(${dark ? 110 : 125}%)`,
      },
    }),
    '@media (prefers-reduced-transparency: reduce)': {
      backgroundColor: solid, backgroundImage: 'none', backdropFilter: 'none', WebkitBackdropFilter: 'none',
    },
    '@media (forced-colors: active)': {
      background: 'Canvas', borderColor: 'ButtonText', boxShadow: 'none', backdropFilter: 'none', WebkitBackdropFilter: 'none',
    },
  };
};

export const glassChromeControl = (theme, { size = 32, radius = glassChromeRadii.control, active = false } = {}) => {
  const { dark, ink, solid, rim, highlight } = chromeColors(theme);
  return {
    width: size, height: size, flexShrink: 0,
    padding: '6px', boxSizing: 'border-box', borderRadius: `${radius}px`,
    color: active ? ink : theme.palette.text.secondary,
    border: `1px solid ${active ? chroma(ink).alpha(0.35).css() : rim}`,
    backgroundColor: solid,
    backgroundImage: `linear-gradient(150deg, ${highlight}, transparent 70%), linear-gradient(${chroma(ink).alpha(active ? 0.14 : 0.025).css()}, ${chroma(ink).alpha(active ? 0.14 : 0.025).css()})`,
    boxShadow: `inset 0 1px 0 ${highlight}, 0 1px 3px ${dark ? '#00000018' : '#1D2B2408'}`,
    transition: 'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      color: ink,
      borderColor: chroma(ink).alpha(0.3).css(),
      backgroundColor: dark ? darkSurfaces.hover : '#FFFFFF',
    },
    '&:active': { boxShadow: `inset 0 1px 3px ${dark ? '#00000040' : '#1D2B2418'}` },
    '&.Mui-focusVisible, &:focus-visible': { outline: `2px solid ${ink}`, outlineOffset: '2px' },
    '&.Mui-disabled': { color: theme.palette.text.disabled, opacity: 0.5, boxShadow: 'none' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '@media (prefers-reduced-transparency: reduce)': {
      backgroundColor: solid, backgroundImage: 'none',
      '&:hover': { backgroundColor: solid },
    },
    '@media (forced-colors: active)': {
      background: 'Canvas', color: 'ButtonText', borderColor: active ? 'Highlight' : 'ButtonText', boxShadow: 'none',
      '&:hover': { background: 'Canvas', color: 'Highlight', borderColor: 'Highlight' },
      '&.Mui-focusVisible, &:focus-visible': { outlineColor: 'Highlight' },
      '&.Mui-disabled': { color: 'GrayText', borderColor: 'GrayText' },
    },
  };
};
